/**
 * C5 §4 — server-clock trust + time-based outage framing.
 *
 * Framing is derived from WHEN an outage started, not authored per incident:
 * unplanned downtime inside the business window (Mon–Fri 07:00–21:00 ET) reads
 * as "server issues"; anything outside it (weekends + overnight) reads as
 * "scheduled maintenance". Both are low-harm if the clock is ever wrong (each
 * says "we're down, back soon"), but we anchor to server time when we can.
 *
 * `serverClockOffset` is sampled from the most recent response's `Date` header
 * (success OR error-with-response). `anchorNow()` = Date.now() + offset. This
 * offset is the TEST SEAM for framing: inject an anchor by setting the offset,
 * never by stubbing Date.now.
 */

let serverClockOffset = 0;

/** Sample the server/local clock skew from a response `Date` header. */
export function sampleServerDate(dateHeader: string | undefined | null): void {
  if (!dateHeader) return;
  const serverMs = Date.parse(dateHeader);
  if (Number.isNaN(serverMs)) return;
  serverClockOffset = serverMs - Date.now();
}

/** The best estimate of the current server instant. */
export function anchorNow(): number {
  return Date.now() + serverClockOffset;
}

export function getServerClockOffset(): number {
  return serverClockOffset;
}

/** Test seam: pin the offset so `anchorNow()` lands on a chosen instant. */
export function setServerClockOffsetForTest(offsetMs: number): void {
  serverClockOffset = offsetMs;
}

export function resetServerClock(): void {
  serverClockOffset = 0;
}

/**
 * C5 §4 — verbatim. America/New_York handles DST via Intl; extract the ET
 * wall-clock parts of the anchor instant.
 */
export function outageFraming(anchorMs: number): 'server_issues' | 'maintenance' {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    hour12: false,
  }).formatToParts(new Date(anchorMs));
  const weekday = p.find((x) => x.type === 'weekday')!.value; // 'Mon'..'Sun'
  const hour = Number(p.find((x) => x.type === 'hour')!.value) % 24; // normalize '24' midnight → 0
  const isWeekday = !['Sat', 'Sun'].includes(weekday);
  const inWindow = hour >= 7 && hour < 21; // [07:00, 21:00) ET
  return isWeekday && inWindow ? 'server_issues' : 'maintenance';
}
